import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    User,
    FileText,
    Shield,
    Award,
    Activity,
    ChevronLeft,
    Flag,
    AlertCircle,
    CheckCircle,
    XCircle,
    Download,
    ExternalLink,
    Mail,
    Briefcase,
    MapPin,
    Clock,
    Trash2,
    Lock,
    Unlock,
    MessageSquare,
    X,
    Eye,
    EyeOff,
    CreditCard,
    Phone
} from 'lucide-react';
import api from '../api';
import Loader from '../components/Loader';
import { EXPERTISE_TAXONOMY } from '../constants/expertise';
import { INDUSTRY_DOMAINS } from '../constants/domains';
import './ProfessorProfileView.css';

const ProfessorProfileView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [professor, setProfessor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [isUpdating, setIsUpdating] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [toasts, setToasts] = useState([]);
    
    const [isEditingTaxonomy, setIsEditingTaxonomy] = useState(false);
    const [taxonomyForm, setTaxonomyForm] = useState({
        domain: '',
        expertise_category: '',
        expertise_subcategory: '',
        expertise_core: ''
    });

    const CATEGORIES = Object.keys(EXPERTISE_TAXONOMY);
    const getSubcategories = (category) => category ? Object.keys(EXPERTISE_TAXONOMY[category] || {}) : [];
    const getCoreExpertise = (category, subcategory) => (category && subcategory && EXPERTISE_TAXONOMY[category]) ? (EXPERTISE_TAXONOMY[category][subcategory] || []) : [];

    // Dynamic Taxonomy Lists
    const [dynIndustries, setDynIndustries] = useState([]);
    const [dynCategories, setDynCategories] = useState([]);
    const [dynSubcategories, setDynSubcategories] = useState([]);

    useEffect(() => {
        api.get('/expertise-categories').then(res => {
            setDynIndustries(res.data || []);
        }).catch(err => console.error("Could not load dynamic categories:", err));
    }, []);

    useEffect(() => {
        if (!taxonomyForm.expertise_category) {
            setDynCategories([]);
            return;
        }
        const ind = dynIndustries.find(i => i.name === taxonomyForm.expertise_category);
        if (ind) {
            api.get(`/expertise-subcategories?categoryId=${ind.id}`).then(res => {
                setDynCategories(res.data || []);
            }).catch(err => console.error(err));
        } else {
            setDynCategories([]);
        }
    }, [taxonomyForm.expertise_category, dynIndustries]);

    useEffect(() => {
        if (!taxonomyForm.expertise_subcategory) {
            setDynSubcategories([]);
            return;
        }
        const cat = dynCategories.find(c => c.name === taxonomyForm.expertise_subcategory);
        if (cat) {
            api.get(`/core-expertise?subcategoryId=${cat.id}`).then(res => {
                setDynSubcategories(res.data || []);
            }).catch(err => console.error(err));
        } else {
            setDynSubcategories([]);
        }
    }, [taxonomyForm.expertise_subcategory, dynCategories]);

    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 4000);
    };

    useEffect(() => {
        fetchProfessorDetails();
    }, [id]);

    const fetchProfessorDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/professor/${id}`);
            setProfessor(response.data);
            setAdminNotes(response.data.admin_notes || '');
            setError(null);
        } catch (err) {
            console.error('Error fetching professor details:', err);
            setError(err.response?.data?.detail || 'Failed to load professor details');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (statusUpdates) => {
        try {
            setIsUpdating(true);
            await api.put(`/admin/professor/${id}/status`, statusUpdates);
            await fetchProfessorDetails();
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to update status', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleTaxonomySave = async () => {
        try {
            setIsUpdating(true);
            await api.put(`/admin/professor/${id}/taxonomy`, taxonomyForm);
            await fetchProfessorDetails();
            setIsEditingTaxonomy(false);
            showToast('Taxonomy updated successfully', 'success');
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to update taxonomy', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSaveNotes = async () => {
        try {
            setIsUpdating(true);
            await api.post(`/admin/professor/${id}/notes`, { admin_notes: adminNotes });
            showToast('Notes saved successfully', 'success');
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to save notes', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            setIsUpdating(true);
            await api.delete(`/admin/${id}/reject`);

            navigate('/admin-dashboard');
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to delete account', 'error');
        } finally {
            setIsUpdating(false);
            setShowDeleteModal(false);
        }
    };

    const handleDocumentAction = async (path, action = 'view') => {
        if (!path) return;

        // If it's an external link, just open it in a new tab
        if (path.startsWith('http')) {
            window.open(path, '_blank');
            return;
        }

        try {
            // Robust filename extraction handling both / and \ 
            const lastSlash = path.lastIndexOf('/');
            const lastBackslash = path.lastIndexOf('\\');
            const transitionIndex = Math.max(lastSlash, lastBackslash);
            const fileName = transitionIndex === -1 ? path : path.substring(transitionIndex + 1);

            // Fetch the document with authentication
            const response = await api.get(`/admin/professor/document/${professor.id}/${encodeURIComponent(fileName)}`, {
                responseType: 'blob'
            });

            const contentType = response.headers['content-type'] || getMimeType(fileName);
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            if (action === 'download') {
                downloadFile(url, fileName);
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            } else {
                // View action
                const canView = contentType.includes('pdf') || contentType.includes('image');
                if (canView) {
                    setViewingDoc({ url, fileName, contentType });
                } else {
                    // Fallback to download for non-viewable types
                    downloadFile(url, fileName);
                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                    showToast('This file type cannot be viewed directly. Starting download...', 'success');
                }
            }
        } catch (err) {
            console.error('Document access error:', err);
            showToast('Failed to access document. It may have been moved or deleted from the server.', 'error');
        }
    };

    const downloadFile = (url, path) => {
        const link = document.createElement('a');
        link.href = url;
        // Handle both / and \ for the suggested download filename
        const fileName = path.split(/[/\\]/).pop();
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getMimeType = (path) => {
        const ext = path.split('.').pop().toLowerCase();
        const mimeTypes = {
            'pdf': 'application/pdf',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };
        return mimeTypes[ext] || 'application/octet-stream';
    };

    if (loading) return <Loader />;

    if (error) return (
        <div className="profile-view-error">
            <XCircle size={48} color="#ef4444" />
            <h2>Access Denied</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/admin-dashboard')} className="back-btn">
                <ChevronLeft size={16} /> Return to Dashboard
            </button>
        </div>
    );

    if (!professor) return null;

    const sections = [
        { id: 'profile', label: 'Profile Intelligence', icon: <User size={18} /> },
        { id: 'documents', label: 'KYC & Verification', icon: <FileText size={18} /> },
        { id: 'coi', label: 'COI / Disclosures', icon: <Shield size={18} /> },
        { id: 'training', label: 'Training Letter', icon: <Clock size={20} /> },
        { id: 'bank', label: 'Bank Details', icon: <CreditCard size={20} /> },
        { id: 'activity', label: 'System Activity', icon: <Activity size={20} /> }
    ];

    return (
        <div className="professor-profile-view">
            {/* Header */}
            <header className="profile-header">
                <div className="header-left">
                    <button onClick={() => navigate('/admin-dashboard')} className="back-btn">
                        <ChevronLeft size={20} />
                    </button>
                    <div className="profile-intro">
                        <h1>{professor.full_name}</h1>
                        <div className="profile-badges">
                            <span className={`badge ${professor.is_active ? 'active' : 'inactive'}`}>
                                {professor.is_active ? 'Active Node' : 'Inactive'}
                            </span>
                            {professor.is_flagged && <span className="badge flagged">Flagged</span>}
                            {professor.is_suspended && <span className="badge suspended">Suspended</span>}
                            {professor.is_verified && <span className="badge verified">Verified</span>}
                            {professor.is_visible && <span className="badge" style={{ backgroundColor: '#10b981', color: 'white', border: '1px solid #059669' }}>PoP Published</span>}
                            {professor.show_in_browse && <span className="badge" style={{ backgroundColor: '#8b5cf6', color: 'white', border: '1px solid #7c3aed' }}>In Browse</span>}
                        </div>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className={`admin-action-btn ${professor.is_flagged ? 'active' : ''}`}
                        onClick={() => handleStatusUpdate({ is_flagged: !professor.is_flagged })}
                        disabled={isUpdating}
                    >
                        <Flag size={18} /> {professor.is_flagged ? 'Unflag' : 'Flag'}
                    </button>
                    <button
                        className={`admin-action-btn ${professor.is_suspended ? 'active' : ''}`}
                        onClick={() => handleStatusUpdate({ is_suspended: !professor.is_suspended })}
                        disabled={isUpdating}
                    >
                        {professor.is_suspended ? <Unlock size={18} /> : <Lock size={18} />}
                        {professor.is_suspended ? 'Unsuspend' : 'Suspend'}
                    </button>
                    <button
                        className={`admin-action-btn ${professor.is_verified ? 'active' : ''}`}
                        onClick={() => handleStatusUpdate({ is_verified: !professor.is_verified })}
                        disabled={isUpdating}
                    >
                        {professor.is_verified ? <XCircle size={18} /> : <CheckCircle size={18} />}
                        {professor.is_verified ? 'Unverify' : 'Verify Identity'}
                    </button>
                    <button
                        className={`admin-action-btn ${professor.show_in_browse ? 'active' : ''}`}
                        onClick={() => handleStatusUpdate({ show_in_browse: !professor.show_in_browse })}
                        disabled={isUpdating}
                    >
                        {professor.show_in_browse ? <EyeOff size={18} /> : <Eye size={18} />}
                        {professor.show_in_browse ? 'Hide from Browse' : 'Show in Browse'}
                    </button>
                    <button
                        className="admin-action-btn danger"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isUpdating}
                    >
                        <Trash2 size={18} /> Purge Account
                    </button>
                </div>
            </header>

            <div className="profile-main-grid">
                {/* Sidebar Navigation */}
                <aside className="profile-sidebar">
                    <nav className="tab-nav">
                        {sections.map(section => (
                            <button
                                key={section.id}
                                className={`nav-item ${activeTab === section.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(section.id)}
                            >
                                {section.icon}
                                <span>{section.label}</span>
                            </button>
                        ))}
                    </nav>

                    <div className="admin-notes-section">
                        <h3><MessageSquare size={16} /> Admin Notes</h3>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            placeholder="Add internal intelligence notes..."
                        />
                        <button onClick={handleSaveNotes} disabled={isUpdating} className="save-notes-btn">
                            Save Changes
                        </button>
                    </div>
                </aside>

                {/* Content Area */}
                <main className="profile-content">
                    {activeTab === 'profile' && (
                        <div className="tab-pane">
                            <section className="info-section">
                                <h2>Professional Coordinates</h2>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>Full Identity</label>
                                        <p>{professor.full_name}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Email Address</label>
                                        <p><Mail size={14} /> {professor.email}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Phone Number</label>
                                        <p><Phone size={14} /> {professor.phone_number || 'Not provided'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Job Designation</label>
                                        <p><Briefcase size={14} /> {professor.job_title} @ {professor.company}</p>
                                    </div>
                                    <div className="info-item full" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: '15px', marginTop: '15px' }}>
                                        <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-color)' }}>Domain & Expertise Taxonomy</h3>
                                        {!isEditingTaxonomy ? (
                                            <button className="admin-action-btn" onClick={() => {
                                                setTaxonomyForm({
                                                    domain: professor.domain || '',
                                                    expertise_category: professor.expertise_category || '',
                                                    expertise_subcategory: professor.expertise_subcategory || '',
                                                    expertise_core: professor.expertise_core || ''
                                                });
                                                setIsEditingTaxonomy(true);
                                            }}>Edit Taxonomy</button>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <button className="admin-action-btn secondary" onClick={() => setIsEditingTaxonomy(false)}>Cancel</button>
                                                <button className="admin-action-btn highlight" onClick={handleTaxonomySave} disabled={isUpdating}>Save</button>
                                            </div>
                                        )}
                                    </div>
                                    {isEditingTaxonomy ? (
                                        <>
                                            <div className="info-item">
                                                <label>Academic Domain</label>
                                                <select 
                                                    className="admin-input" 
                                                    value={taxonomyForm.domain}
                                                    onChange={(e) => setTaxonomyForm({ ...taxonomyForm, domain: e.target.value })}
                                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                                                >
                                                    <option value="">Select Domain</option>
                                                    {INDUSTRY_DOMAINS.map(dom => <option key={dom} value={dom}>{dom}</option>)}
                                                </select>
                                            </div>
                                            <div className="info-item">
                                                <label>Expertise Category</label>
                                                <select 
                                                    className="admin-input" 
                                                    value={taxonomyForm.expertise_category}
                                                    onChange={(e) => setTaxonomyForm({ ...taxonomyForm, expertise_category: e.target.value, expertise_subcategory: '', expertise_core: '' })}
                                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                                                >
                                                    <option value="">Select Category</option>
                                                    {dynCategories.length > 0
                                                        ? dynCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)
                                                        : CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                                </select>
                                            </div>
                                            <div className="info-item">
                                                <label>Sub-category</label>
                                                <select 
                                                    className="admin-input" 
                                                    value={taxonomyForm.expertise_subcategory}
                                                    onChange={(e) => setTaxonomyForm({ ...taxonomyForm, expertise_subcategory: e.target.value, expertise_core: '' })}
                                                    disabled={!taxonomyForm.expertise_category}
                                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                                                >
                                                    <option value="">Select Sub-category</option>
                                                    {dynSubcategories.length > 0
                                                        ? dynSubcategories.map(sub => <option key={sub.id} value={sub.name}>{sub.name}</option>)
                                                        : getSubcategories(taxonomyForm.expertise_category).map(sub => <option key={sub} value={sub}>{sub}</option>)}
                                                </select>
                                            </div>
                                            <div className="info-item">
                                                <label>Core Expertise</label>
                                                <select 
                                                    className="admin-input" 
                                                    value={taxonomyForm.expertise_core}
                                                    onChange={(e) => setTaxonomyForm({ ...taxonomyForm, expertise_core: e.target.value })}
                                                    disabled={!taxonomyForm.expertise_subcategory}
                                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-color)' }}
                                                >
                                                    <option value="">Select Core</option>
                                                    {dynSubcategories.length > 0
                                                        ? dynSubcategories.map(core => <option key={core.id} value={core.name}>{core.name}</option>)
                                                        : getCoreExpertise(taxonomyForm.expertise_category, taxonomyForm.expertise_subcategory).map(core => <option key={core} value={core}>{core}</option>)}
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="info-item">
                                                <label>Academic Domain</label>
                                                <p>{professor.domain || 'Not Set'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>Expertise Category</label>
                                                <p>{professor.expertise_category || 'Not Set'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>Sub-category</label>
                                                <p>{professor.expertise_subcategory || 'Not Set'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>Core Expertise</label>
                                                <p>{professor.expertise_core || 'Not Set'}</p>
                                            </div>
                                        </>
                                    )}
                                    <div className="info-item">
                                        <label>Years of Practice</label>
                                        <p>{professor.years_of_experience} Years</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Locality</label>
                                        <p><MapPin size={14} /> {professor.city}, {professor.state}, {professor.country}</p>
                                    </div>
                                    <div className="info-item full">
                                        <label>Personal Dossier / Bio</label>
                                        <p>{professor.bio || 'No intelligence available.'}</p>
                                    </div>
                                </div>
                            </section>

                            <section className="info-section">
                                <h2>Technical Capabilities</h2>
                                <div className="skills-cloud">
                                    {professor.expertise_areas?.split(',').map((skill, idx) => (
                                        <span key={idx} className="skill-tag">{skill.trim()}</span>
                                    ))}
                                </div>
                            </section>

                            <section className="info-section">
                                <h2>Availability Intelligence</h2>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>Global Weekly Capacity</label>
                                        <p>{professor.weekly_quota_hours ? `${professor.weekly_quota_hours} Hours / Week` : 'Unlimited'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Availability Window</label>
                                        <p>
                                            {professor.window_start_date ? new Date(professor.window_start_date).toLocaleDateString() : 'Immediate'}
                                            {' \u2192 '}
                                            {professor.window_end_date ? new Date(professor.window_end_date).toLocaleDateString() : 'Indefinite'}
                                        </p>
                                    </div>
                                </div>
                            </section>
                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div className="tab-pane">
                            <section className="info-section">
                                <h2>KYC Verification</h2>
                                <div className="document-list">
                                    <div className="doc-card">
                                        <div className="doc-info">
                                            <FileText size={24} color="#6366f1" />
                                            <div>
                                                <h4>PAN Card</h4>
                                                <p>{professor.pan_card_path ? 'Identity Verified' : 'Missing Intelligence'}</p>
                                            </div>
                                        </div>
                                        <div className="doc-actions">
                                            {professor.pan_card_path ? (
                                                <div className="action-group">
                                                    <button onClick={() => handleDocumentAction(professor.pan_card_path, 'view')} className="view-btn">
                                                        <Eye size={16} /> View
                                                    </button>
                                                    <button onClick={() => handleDocumentAction(professor.pan_card_path, 'download')} className="download-btn">
                                                        <Download size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="badge-missing">Missing</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="doc-card">
                                        <div className="doc-info">
                                            <Shield size={24} color="#6366f1" />
                                            <div>
                                                <h4>Aadhaar Card</h4>
                                                <p>{professor.aadhaar_card_path ? 'Resident Identity Verified' : 'Missing Intelligence'}</p>
                                            </div>
                                        </div>
                                        <div className="doc-actions">
                                            {professor.aadhaar_card_path ? (
                                                <div className="action-group">
                                                    <button onClick={() => handleDocumentAction(professor.aadhaar_card_path, 'view')} className="view-btn">
                                                        <Eye size={16} /> View
                                                    </button>
                                                    <button onClick={() => handleDocumentAction(professor.aadhaar_card_path, 'download')} className="download-btn">
                                                        <Download size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="badge-missing">Missing</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section className="info-section">
                                <h2>Credential Verification</h2>
                                <div className="document-list vertical">
                                    {[
                                        { label: 'Curriculum Vitae', path: professor.cv_resume_path || professor.resume_link },
                                        { label: 'Degree Certificates', path: professor.degree_certificates_path }
                                    ].map((doc, idx) => (
                                        <div key={idx} className="doc-card compact">
                                            <div className="doc-info">
                                                <FileText size={20} />
                                                <h4>{doc.label}</h4>
                                            </div>
                                            <div className="doc-actions">
                                                {doc.path ? (
                                                    <div className="action-group-compact">
                                                        <button onClick={() => handleDocumentAction(doc.path, 'view')} className="icon-btn" title="View">
                                                            <Eye size={16} />
                                                        </button>
                                                        <button onClick={() => handleDocumentAction(doc.path, 'download')} className="icon-btn highlight" title="Download">
                                                            <Download size={16} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">Not Found</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>
                    )}

                    {activeTab === 'coi' && (
                        <div className="tab-pane">
                            <section className="info-section center">
                                <Shield size={64} color="#6366f1" className="mb-4" />
                                <h2>Conflict of Interest Intelligence</h2>
                                <p className="mb-6">Verify if the professor has any conflicting affiliations or disclosures.</p>

                                {professor.coi_document_path ? (
                                    <div className="coi-display">
                                        <div className="coi-header">
                                            <CheckCircle size={20} color="#10b981" />
                                            <span>COI Dossier Submitted</span>
                                        </div>
                                        <div className="action-group center" style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '20px' }}>
                                            <button
                                                onClick={() => handleDocumentAction(professor.coi_document_path, 'view')}
                                                className="main-download-btn secondary"
                                            >
                                                <Eye size={18} /> View Dossier
                                            </button>
                                            <button
                                                onClick={() => handleDocumentAction(professor.coi_document_path, 'download')}
                                                className="main-download-btn"
                                            >
                                                <Download size={18} /> Download Disclosure
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="coi-missing">
                                        <AlertCircle size={32} color="#f59e0b" />
                                        <p>No Conflict of Interest disclosure has been uploaded by this user.</p>
                                    </div>
                                )}
                            </section>
                        </div>
                    )}

                    {activeTab === 'training' && (
                        <div className="tab-pane">
                            <section className="info-section center">
                                <Award size={64} color="#6366f1" className="mb-4" />
                                <h2>Training Certification Letter</h2>
                                <p className="mb-6">Verification of completion for mandatory professor-of-practice training modules.</p>

                                {professor.training_letter_path ? (
                                    <div className="training-display">
                                        <div className="training-summary">
                                            <div className="summary-item">
                                                <label>Execution Date</label>
                                                <span>{new Date(professor.training_letter_signed_at).toLocaleDateString()}</span>
                                            </div>
                                            <div className="summary-item">
                                                <label>System Integrity</label>
                                                <span className="text-green-500">Verified Signature</span>
                                            </div>
                                        </div>
                                        <div className="action-group center">
                                            <button
                                                onClick={() => handleDocumentAction(professor.training_letter_path, 'view')}
                                                className="main-download-btn secondary success"
                                            >
                                                <Eye size={18} /> View Letter
                                            </button>
                                            <button
                                                onClick={() => handleDocumentAction(professor.training_letter_path, 'download')}
                                                className="main-download-btn success"
                                            >
                                                <Download size={18} /> Download Execution
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="training-missing">
                                        <Clock size={32} color="var(--text-muted)" />
                                        <p>Mandatory training certification letter has not been generated or signed.</p>
                                    </div>
                                )}
                            </section>
                        </div>
                    )}

                    {activeTab === 'bank' && (
                        <div className="tab-pane">
                            <section className="info-section">
                                <h2>Bank Details</h2>
                                {professor.bank_details ? (
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Account Holder Name</label>
                                            <p>{professor.bank_details.account_holder_name}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Bank Name</label>
                                            <p>{professor.bank_details.bank_name}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Account Number</label>
                                            <p style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
                                                {professor.bank_details.account_number}
                                            </p>
                                        </div>
                                        <div className="info-item">
                                            <label>IFSC Code</label>
                                            <p style={{ fontFamily: 'monospace' }}>{professor.bank_details.ifsc_code}</p>
                                        </div>
                                        {professor.bank_details.branch_name && (
                                            <div className="info-item">
                                                <label>Branch Name</label>
                                                <p>{professor.bank_details.branch_name}</p>
                                            </div>
                                        )}
                                        {professor.bank_details.account_type && (
                                            <div className="info-item">
                                                <label>Account Type</label>
                                                <p>{professor.bank_details.account_type}</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--engine-black)', borderRadius: '12px', border: '2px dashed var(--border)' }}>
                                        <CreditCard size={48} color="var(--text-faint)" style={{ marginBottom: '1rem' }} />
                                        <p style={{ color: 'var(--text-muted)' }}>No bank details linked to this account.</p>
                                    </div>
                                )}
                            </section>
                        </div>
                    )}

                    {activeTab === 'activity' && (
                        <div className="tab-pane">
                            <section className="info-section">
                                <h2>Account Lifecycle</h2>
                                <div className="activity-timeline">
                                    <div className="timeline-item">
                                        <div className="time-marker"></div>
                                        <div className="activity-details">
                                            <label>{new Date(professor.created_at).toLocaleString()}</label>
                                            <p>Node initialised in system database.</p>
                                        </div>
                                    </div>
                                    <div className="timeline-item">
                                        <div className="time-marker"></div>
                                        <div className="activity-details">
                                            <label>N/A</label>
                                            <p>Awaiting further intelligence logs...</p>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    )}
                </main>
            </div>

            {/* Purge Confirmation Modal */}
            {showDeleteModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal purge">
                        <div className="purge-icon-wrapper">
                            <Trash2 size={48} />
                        </div>
                        <h2>Initiate Purge Sequence?</h2>
                        <p>This will permanently erase the professor's identity and all associated dossiers from the secure database. This action is IRREVOCABLE.</p>
                        <div className="modal-actions">
                            <button onClick={() => setShowDeleteModal(false)} className="cancel-btn">
                                Abort Mission
                            </button>
                            <button onClick={handleDeleteAccount} className="confirm-btn danger" disabled={isUpdating}>
                                {isUpdating ? 'Purging...' : 'Execute Purge'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Document Viewer Modal */}
            {viewingDoc && (
                <div className="doc-viewer-overlay">
                    <div className="doc-viewer-modal">
                        <div className="viewer-header">
                            <h3>{viewingDoc.fileName}</h3>
                            <div className="viewer-actions">
                                <button
                                    onClick={() => downloadFile(viewingDoc.url, viewingDoc.fileName)}
                                    className="viewer-download-btn"
                                    title="Download"
                                >
                                    <Download size={20} />
                                </button>
                                <button
                                    onClick={() => {
                                        window.URL.revokeObjectURL(viewingDoc.url);
                                        setViewingDoc(null);
                                    }}
                                    className="viewer-close-btn"
                                    title="Close"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>
                        <div className="viewer-content">
                            {viewingDoc.contentType.includes('pdf') ? (
                                <iframe src={viewingDoc.url} title="Document Viewer" />
                            ) : (
                                <img src={viewingDoc.url} alt="Document" />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
            }}>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        style={{
                            background: toast.type === 'success' ? '#10b981' : '#ef4444',
                            color: '#fff',
                            padding: '16px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            minWidth: '300px',
                            maxWidth: '500px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            animation: 'slideIn 0.3s ease-out',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        <span style={{ fontSize: '20px' }}>
                            {toast.type === 'success' ? '\u2713' : '\u2715'}
                        </span>
                        <span style={{ flex: 1 }}>{toast.message}</span>
                        <button
                            onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '18px',
                                padding: '0',
                                opacity: 0.8,
                                transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '1'}
                            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProfessorProfileView;

